import {
  Center,
  Flex,
  Text,
  Paper,
  Stack,
  TextInput,
  PasswordInput,
  Alert,
  Button,
  Anchor,
} from "@mantine/core";
import { IconAlertTriangle, IconCheck } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { useState, Suspense } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { nprogress } from "@mantine/nprogress";
import { signupSchema } from "../../schemas/auth.schema";
import { Navigate, Link, useNavigate } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import { AxiosError } from "axios";
import { useSessionStore } from "../../store/session.store";
import { routePaths } from "../../constants/paths";

function SignupForm() {
  const { sessionExpiryDate } = useSessionStore((state) => state);

  const [visible, { toggle }] = useDisclosure(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const { signupUser } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
    resolver: yupResolver(signupSchema),
  });

  const onSubmit = (values: Yup.InferType<typeof signupSchema>) => {
    setError("");
    setLoading(true);
    nprogress.start();

    signupUser({
      values: {
        name: values.name,
        email: values.email,
        phone: values.phone,
        password: values.password,
      },
    })
      .then((res) => {
        if (res !== undefined && !(res as any)?.success) {
          const errorMessage =
            res instanceof AxiosError
              ? res?.response?.data?.message ||
                res?.response?.statusText ||
                "Registration failed!"
              : "Registration failed!";
          setError(errorMessage);
          nprogress.complete();
        } else {
          reset();
          nprogress.complete();
          setSuccess(true);
          setTimeout(() => {
            navigate(routePaths.login);
          }, 2000);
        }
      })
      .finally(() => {
        setLoading(false);
        nprogress.complete();
      });
  };

  if (sessionExpiryDate && new Date(sessionExpiryDate) > new Date()) {
    return <Navigate to={routePaths.dashboard} />;
  }

  return (
    <Center mih={"100vh"} h={"100%"}>
      <Flex
        w={{ base: "100%", md: "30%" }}
        direction={"column"}
        align={"center"}
        justify={"center"}
        mx={10}
      >
        <Paper w={"100%"} radius="lg" p="xl" withBorder>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack>
              <Text c={"blue"} fw={800} ta={"center"} size="xl">
                Create an account
              </Text>
              <TextInput
                label="Full Name"
                placeholder="Your full name"
                {...register("name")}
                error={errors.name?.message}
              />
              <TextInput
                label="Email"
                type="email"
                placeholder="Your email address"
                {...register("email")}
                error={errors.email?.message}
              />
              <TextInput
                label="Phone"
                placeholder="Your phone number"
                {...register("phone")}
                error={errors.phone?.message}
              />
              <PasswordInput
                label="Password"
                placeholder="Create a password"
                visible={visible}
                onVisibilityChange={toggle}
                {...register("password")}
                error={errors.password?.message}
              />
              <PasswordInput
                label="Confirm Password"
                placeholder="Confirm your password"
                visible={visible}
                onVisibilityChange={toggle}
                {...register("confirmPassword")}
                error={errors.confirmPassword?.message}
              />
              {error ? (
                <Alert
                  variant="light"
                  color="red"
                  title={error}
                  icon={<IconAlertTriangle />}
                />
              ) : null}
              {success ? (
                <Alert
                  variant="light"
                  color="green"
                  title="Registration Successful"
                  icon={<IconCheck />}
                >
                  Your account has been created. Redirecting to login...
                </Alert>
              ) : null}
              <Button type="submit" radius="sm" loading={loading}>
                Sign Up
              </Button>
              <Text ta="center" size="sm">
                Already have an account?{" "}
                <Anchor component={Link} to={routePaths.login}>
                  Sign in
                </Anchor>
              </Text>
            </Stack>
          </form>
        </Paper>
      </Flex>
    </Center>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SignupForm />
    </Suspense>
  );
}
